import {
  createFailClosedAdvertisingAdapter,
  createFailClosedPaymentAdapter,
  sanitizeAnalytics,
  type AnalyticsValue,
  type PlatformAdapter,
} from '@petrhythm/core';
import { playJuice } from './juice.js';
import { downloadBlob } from './download.js';

const subjectKey = 'petrhythm.install_id';

function getInstallId(): string {
  const stored = localStorage.getItem(subjectKey);
  if (stored) return stored;
  const generated = crypto.randomUUID();
  localStorage.setItem(subjectKey, generated);
  return generated;
}

function base64UrlToBytes(value: string): Uint8Array {
  const padded = value.replaceAll('-', '+').replaceAll('_', '/') + '='.repeat((4 - (value.length % 4)) % 4);
  const binary = atob(padded);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

async function syncSubscription(subscription: PushSubscription, timezone: string): Promise<void> {
  const api = import.meta.env.VITE_API_BASE_URL as string | undefined;
  const token = localStorage.getItem('petrhythm.subject_token');
  if (!api || !token) throw new Error('notification transport unavailable');
  const json = subscription.toJSON();
  const response = await fetch(`${api}/v1/notify/subscribe`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ consentVersion:'webpush-2026-10',channel: 'webpush', endpoint: json.endpoint, p256dh: json.keys?.p256dh, auth: json.keys?.auth, timezone }),
  });
  if (!response.ok) throw new Error('notification subscription sync failed');
}

export const webAdapter: PlatformAdapter = {
  files: { async save(filename, blob) { try { downloadBlob(filename, blob); return { status: 'requested' }; } catch { return { status: 'failed', message: '다운로드를 요청하지 못했어요. 다시 시도해 주세요.' }; } } },
  auth: {
    kind: 'anonymous',
    async getSubject() { return getInstallId(); },
    async signOut() { /* 익명 설치 식별자는 로그아웃하지 않는다. */ },
  },
  notify: {
    kind: 'web-push',
    async requestPermission() {
      if (typeof Notification === 'undefined' || (await Notification.requestPermission()) !== 'granted') return false;
      const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
      if (!vapidKey || !('serviceWorker' in navigator) || !('PushManager' in window)) return false;
      try {
        const {ensureSubjectToken}=await import('./remote.js');await ensureSubjectToken();
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(vapidKey).buffer as ArrayBuffer });
        await syncSubscription(subscription, Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Seoul');
        localStorage.setItem('petrhythm.notifications', 'on');
        localStorage.setItem('petrhythm.notify-connected-at',String(Date.now()));
        localStorage.removeItem('petrhythm.notify-plan-signature');
        return true;
      } catch {
        localStorage.setItem('petrhythm.notifications', 'off');
        return false;
      }
    },
    async unsubscribe() {
      localStorage.setItem('petrhythm.notifications', 'off');
      if ('serviceWorker' in navigator) {
        try { await (await navigator.serviceWorker.ready).pushManager.getSubscription().then((subscription) => subscription?.unsubscribe()); } catch { /* provider 미연결 */ }
      }
      const api = import.meta.env.VITE_API_BASE_URL as string | undefined;
      const token = localStorage.getItem('petrhythm.subject_token');
      if(api&&token){const response=await fetch(`${api}/v1/notify/subscribe`,{method:'DELETE',headers:{authorization:`Bearer ${token}`}});if(!response.ok)throw new Error('알림 해제를 확인하지 못했어요.');}
      for(const key of ['petrhythm.notify_slots','petrhythm.notify-plan-signature'])localStorage.removeItem(key);
    },
    async sendTest() {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') new Notification('오늘 기록', { body: '알림 연결을 확인했어요.' });
    },
  },
  payment: {
    ...createFailClosedPaymentAdapter('web'),
  },
  advertising: createFailClosedAdvertisingAdapter(),
  share: {
    async share(payload) {
      if (navigator.share && (!payload.files?.length || !navigator.canShare || navigator.canShare({ files: payload.files }))) {
        await navigator.share(payload);
        return 'shared';
      }
      await navigator.clipboard.writeText([payload.text ?? payload.title, payload.url].filter(Boolean).join('\n'));
      return 'copied';
    },
  },
  haptic: {
    tap(style) { if ('vibrate' in navigator) navigator.vibrate(style === 'light' ? 10 : 20); },
  },
  externalLinks: { async open() { return false; } },
};

export function bindFailClosedCommerceState(adapter: PlatformAdapter): void {
  const update = () => {
    const snapshot = adapter.payment.evaluate({
      firstValueCompleted: Boolean(localStorage.getItem(FIRST_RECORD_KEY)),
      placement: 'pre_first_value',
      memberOfInitialPublicFreeCohort: true,
    });
    document.documentElement.dataset.monetizationContract = snapshot.contractVersion;
    document.documentElement.dataset.paywall = snapshot.paywall.visible ? 'available' : 'off';
    document.documentElement.dataset.checkout = snapshot.checkout.visible ? 'available' : 'off';
    document.documentElement.dataset.advertising = snapshot.advertising.visible ? 'available' : 'off';
  };
  update();
  window.addEventListener('petrhythm:first-record', update);
}

let activeAdapter: PlatformAdapter = webAdapter;

export function setActivePlatformAdapter(adapter: PlatformAdapter): void {
  activeAdapter = adapter;
}

export function getActivePlatformAdapter(): PlatformAdapter {
  return activeAdapter;
}

export function track(event: string, props: Record<string, AnalyticsValue> = {}): void {
  // Analytics and optional feedback must never turn a committed record into a retry.
  try {
    const channel = document.documentElement.dataset.channel === 'ait' ? 'ait' : 'web';
    const enriched = event === 'record_saved' ? { recordType: 'other', channel, ...props } : event === 'app_opened' ? { channel, isReturning: Boolean(localStorage.getItem(FIRST_RECORD_KEY)), returnBucket: returnBucket(), ...props } : (event === 'share_completed' || event === 'purchase_intent') ? { channel, ...props } : props;
    const payload = sanitizeAnalytics(event, {...enriched,traffic:navigator.webdriver||sessionStorage.getItem('petrhythm.qa')==='true'?'synthetic':'natural'});
    if (!payload) return;
    window.dispatchEvent(new CustomEvent('petrhythm:analytics', { detail: payload }));
    if (event === 'record_saved' || event === 'dose_logged') playJuice('save.success');
    if (event === 'report_generated') playJuice('reveal.done');
    if ((event === 'record_saved' || event === 'dose_logged') && !localStorage.getItem(FIRST_RECORD_KEY)) {
      const openedAt = Number(localStorage.getItem(FIRST_OPEN_KEY));
      const entry = sessionStorage.getItem(ENTRY_KEY) || 'landing';
      const first = sanitizeAnalytics('first_record_completed', { ...(Number.isFinite(openedAt) && openedAt > 0 && openedAt <= Date.now() ? { secondsSinceFirstOpen: Math.round((Date.now() - openedAt) / 1000) } : {}), entry, traffic: navigator.webdriver || sessionStorage.getItem('petrhythm.qa') === 'true' ? 'synthetic' : 'natural' });
      if (first) window.dispatchEvent(new CustomEvent('petrhythm:analytics', { detail: first }));
      localStorage.setItem(FIRST_RECORD_KEY, String(Date.now()));
      window.dispatchEvent(new CustomEvent('petrhythm:first-record'));
    }
  } catch { /* telemetry unavailable; the record is still saved */ }
}

const FIRST_OPEN_KEY = 'petrhythm.first_opened_at';
const ENTRY_KEY = 'petrhythm.entry';

/** 공개 도구에서 넘어온 첫 기록이면 그 출처를 이번 탭에 남긴다(값은 열거만). */
export function markEntry(entry: string): void {
  try { sessionStorage.setItem(ENTRY_KEY, entry.slice(0, 32)); } catch { /* optional measurement */ }
}

/** 첫 열기 시각 기준 재방문 구간. 기기 안에서만 계산하고 날짜는 보내지 않는다. */
export function returnBucket(now = Date.now()): string {
  try {
    const opened = Number(localStorage.getItem(FIRST_OPEN_KEY));
    if (!Number.isFinite(opened) || opened <= 0) return 'd0';
    const days = Math.floor((now - opened) / 86_400_000);
    return days < 1 ? 'd0' : days < 7 ? 'd1_6' : days < 14 ? 'd7_13' : days < 30 ? 'd14_29' : 'd30p';
  } catch { return 'd0'; }
}
const FIRST_RECORD_KEY = 'petrhythm.first_record_completed';

export function markAppOpened(): void {
  try { if (!localStorage.getItem(FIRST_OPEN_KEY)) localStorage.setItem(FIRST_OPEN_KEY, String(Date.now())); } catch { /* optional measurement */ }
}

export function trackRecordSaved(recordType: string): void {
  track('record_saved', { recordType });
}
